import { createSelector } from "@reduxjs/toolkit";
//#region ../src/state/app-selectors.ts
/**
* App-level memoized selectors for CombinedRootState.
*
* Wraps library getter functions with createSelector (RTK/reselect) for
* standard memoization. The library getters already return stable references
* via immutable state, but createSelector adds:
* - Explicit memoization contract (input comparison → cached output)
* - Consistent select* naming convention matching selectCachedKnownRefPoints
* - Composition-ready building blocks for derived selectors
*
* Used by subscribeToSelector in store-subscribers.ts for change detection.
*
* @see docs/2026-04-07-architecture-observations-consolidated.md §1
*/
const EMPTY_GPS_POSITIONS = [];
const EMPTY_ODOM_POSITIONS = [];
const EMPTY_ODOM_ROTATIONS = [];
const EMPTY_REF_POINTS = [];
const selectGpsData = (state) => state.gpsData;
/** Alignment matrix (4×4), or null if not yet computed. */
const selectAlignmentMatrix = createSelector([selectGpsData], (gpsData) => gpsData?.gpsEvents?.alignmentMatrix ?? null);
/** Recorded GPS positions with metadata. */
const selectGpsPositions = createSelector([selectGpsData], (gpsData) => gpsData?.gpsEvents?.gpsPositions ?? EMPTY_GPS_POSITIONS);
/** Recorded odometry positions (AR-local space). */
const selectOdometryPositions = createSelector([selectGpsData], (gpsData) => gpsData?.gpsEvents?.odometryPositions ?? EMPTY_ODOM_POSITIONS);
/** Recorded odometry rotations (AR-local space). */
const selectOdometryRotations = createSelector([selectGpsData], (gpsData) => gpsData?.gpsEvents?.odometryRotations ?? EMPTY_ODOM_ROTATIONS);
/** GPS zero reference (origin for coordinate conversion), or null. */
const selectZeroReference = createSelector([selectGpsData], (gpsData) => gpsData?.zero ?? null);
/** User-defined reference points for ground truth validation. */
const selectReferencePoints = createSelector([selectGpsData], (gpsData) => gpsData?.referencePoints ?? EMPTY_REF_POINTS);
//#endregion
export { selectAlignmentMatrix, selectGpsPositions, selectOdometryPositions, selectOdometryRotations, selectReferencePoints, selectZeroReference };
